/**
 * KhalAppFrame — Runtime v2 iframe renderer for full-stack image apps.
 *
 * Renders a KHAL v2 app inside an iframe, replacing the v1 DynamicAppBundle
 * (dynamic import) pattern. The shell owns window chrome, auth handoff, and
 * lifecycle. The app owns everything inside its document.
 *
 * Protocol:
 *   Shell → App: khal:init (token, user, customer, theme, locale, permissions)
 *   App → Shell: khal:ready (signal that auth exchange completed)
 *   Shell → App: khal:theme-change
 *   App → Shell: khal:notify, khal:title
 *
 * Strict origin validation: messages are only accepted from the expected origin.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { ComponentType } from 'react';
import { useWindowStore } from '@khal-os/desktop-shell';
import { useKhalAuth } from '@khal-os/sdk/app';

interface KhalAppFrameProps {
  windowId: string;
  meta?: Record<string, unknown>;
}

/** Props passed via meta by the desktop shell renderer */
interface V2Meta {
  appId: string;
  launchUrl: string;
  /** One-time launch token minted by the kernel launch route. */
  launchToken?: string;
  /** Endpoint used to fetch a fresh one-time launch descriptor. */
  launchEndpoint?: string;
  /** Expected origin of the app (derived from launchUrl) */
  origin?: string;
}

const IFRAME_TIMEOUT_MS = 15_000;
const INIT_WATCHDOG_MS = 60_000;

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const frameStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  border: 'none',
  display: 'block',
};

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  flexDirection: 'column',
  gap: 12,
  padding: 24,
  color: 'var(--khal-text-secondary, #888)',
  textAlign: 'center' as const,
};

const errorStyle: React.CSSProperties = {
  ...loadingStyle,
  color: '#ff6666',
};

const retryButtonStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 6,
  border: '1px solid var(--khal-border, #333)',
  background: 'var(--khal-surface-elevated, #1f1f2e)',
  color: 'var(--khal-text, #ddd)',
  cursor: 'pointer',
  fontSize: 13,
};

/**
 * Extract origin from a URL string for strict postMessage validation.
 */
function extractOrigin(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.origin;
  } catch {
    return '';
  }
}

/**
 * Derive the expected v2 meta from the window meta prop.
 * This is populated by the appRenderer in main.tsx when runtimeKind === 'v2-iframe'.
 */
function deriveMeta(meta: Record<string, unknown> | undefined): V2Meta | null {
  if (!meta) return null;
  const appId = meta.appId as string | undefined;
  const launchUrl = meta.launchUrl as string | undefined;
  if (!appId || !launchUrl) return null;
  return {
    appId,
    launchUrl,
    launchToken: meta.launchToken as string | undefined,
    launchEndpoint: meta.launchEndpoint as string | undefined,
    origin: (meta.origin as string) || extractOrigin(launchUrl),
  };
}

export const KhalAppFrame: ComponentType<KhalAppFrameProps> = ({ windowId: _windowId, meta }) => {
  const auth = useKhalAuth();
  const updateWindowMeta = useWindowStore((state) => state.updateWindowMeta);
  const focusWindow = useWindowStore((state) => state.focusWindow);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'timeout'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [launchDescriptor, setLaunchDescriptor] = useState<Record<string, unknown> | null>(null);
  const initSentRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initReplayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initWatchdogRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const readyRef = useRef(false);

  const v2 = deriveMeta(launchDescriptor ? { ...meta, ...launchDescriptor } : meta);
  const launchEndpoint = typeof meta?.launchEndpoint === 'string' ? meta.launchEndpoint : undefined;
  const framePaused = meta?.framePaused === true || meta?.iframeParked === true || meta?.parked === true;
  const parkedAppId = typeof meta?.appId === 'string' ? meta.appId : v2?.appId || 'v2 app';
  const parkedLaunchLabel =
    typeof meta?.launchUrl === 'string'
      ? meta.launchUrl
      : typeof meta?.launchEndpoint === 'string'
        ? meta.launchEndpoint
        : v2?.launchUrl || 'launch descriptor pending';

  useEffect(() => {
    if (framePaused) {
      setLaunchDescriptor(null);
      return;
    }
    if (!launchEndpoint) {
      setLaunchDescriptor(null);
      return;
    }
    let cancelled = false;
    setLaunchDescriptor(null);
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (auth?.token) headers.Authorization = `Bearer ${auth.token}`;
    fetch(launchEndpoint, { method: 'GET', credentials: 'include', headers })
      .then((res) => {
        if (!res.ok) throw new Error(`launch descriptor failed: ${res.status}`);
        return res.json() as Promise<Record<string, unknown>>;
      })
      .then((descriptor) => {
        if (!cancelled) setLaunchDescriptor(descriptor);
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorMsg(err instanceof Error ? err.message : String(err));
        setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [framePaused, launchEndpoint, auth?.token]);

  const resumeFrame = useCallback(() => {
    updateWindowMeta(_windowId, { framePaused: false, iframeParked: false, parked: false });
    focusWindow(_windowId);
  }, [_windowId, focusWindow, updateWindowMeta]);

  const buildInitMessage = useCallback(() => {
    if (!v2) return null;
    const platformToken = auth?.token;
    const oneTimeLaunchToken = v2.launchToken;
    if (v2.launchEndpoint && (!platformToken || !oneTimeLaunchToken)) return null;
    const token = platformToken || oneTimeLaunchToken;
    if (!token) return null;
    const user = {
      id: auth?.userId || 'unknown-user',
      email: auth?.email || '',
      name: auth?.name || auth?.email || auth?.userId || 'KHAL User',
    };
    const customer = { id: auth?.orgId || 'unknown-org', name: auth?.orgId || 'KHAL Workspace' };
    const workspace = { id: auth?.orgId || 'unknown-workspace', name: auth?.orgId || 'KHAL Workspace', slug: auth?.orgId || 'khal' };
    const permissions = auth?.permissions?.length ? auth.permissions : [`app:${v2.appId}:open`, `app:${v2.appId}:client`];

    return {
      type: 'khal:init',
      version: 1,
      // Keep the original steel-thread contract.
      token,
      user,
      customer,
      workspace,
      theme: 'dark' as const,
      locale: 'en',
      permissions,
      // Also provide the ADR/RFC-shaped aliases used by newer v2 apps and
      // manual recovery probes. This is intentionally additive so old apps keep
      // working while newer apps can read launchToken/context.
      launchToken: oneTimeLaunchToken || token,
      context: {
        user,
        org: customer,
        customer,
        workspace,
        theme: 'dark' as const,
        locale: 'en',
        permissions,
      },
      capabilities: {},
      // Cost/observability labels (stubs — platform fills at launch per ADR 010)
      labels: {
        'khal.ai/app-id': v2.appId,
        'khal.ai/customer-id': customer.id,
        'khal.ai/app-install-id': `${v2.appId}:${customer.id}`,
      },
    };
  }, [v2, auth?.token, auth?.userId, auth?.email, auth?.name, auth?.orgId, auth?.permissions]);

  const sendInitToFrame = useCallback(() => {
    if (!v2 || !iframeRef.current || readyRef.current) return false;
    if (!v2.origin) return false;
    const initMsg = buildInitMessage();
    if (!initMsg) return false;
    iframeRef.current.contentWindow?.postMessage(initMsg, v2.origin);
    return true;
  }, [v2, buildInitMessage]);

  // Cleanup timeout/replay on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (initReplayRef.current) clearInterval(initReplayRef.current);
      if (initWatchdogRef.current) clearInterval(initWatchdogRef.current);
    };
  }, []);

  // Start a parent-side init watchdog as soon as the iframe element exists.
  // Browser/prod evidence showed iframe load can be a poor boundary here: the
  // app document may be visible and listening while the shell's load-scoped
  // replay has already stopped or never attached to the displayed document.
  useEffect(() => {
    if (!v2?.origin) return;
    if (initWatchdogRef.current) clearInterval(initWatchdogRef.current);
    const startedAt = Date.now();
    initWatchdogRef.current = setInterval(() => {
      if (readyRef.current) {
        if (initWatchdogRef.current) clearInterval(initWatchdogRef.current);
        return;
      }
      const elapsed = Date.now() - startedAt;
      if (elapsed > INIT_WATCHDOG_MS) {
        if (initWatchdogRef.current) clearInterval(initWatchdogRef.current);
        return;
      }
      // If the iframe load handler is active, let its short replay window own
      // the early traffic. After that window, keep sending a low-cost parent
      // poke so late child listeners do not strand the app on "Connecting".
      if (initSentRef.current && elapsed <= IFRAME_TIMEOUT_MS) return;
      sendInitToFrame();
    }, 500);
    return () => {
      if (initWatchdogRef.current) clearInterval(initWatchdogRef.current);
    };
  }, [v2?.appId, v2?.launchUrl, v2?.launchToken, v2?.origin, sendInitToFrame]);

  // Listen for postMessage responses from the iframe
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!v2?.origin) return;

      // Only accept messages from our specific iframe. Check source before
      // warning on origin mismatch so normal sibling iframe chatter does not
      // spam the console in multi-window runtime dogfood.
      if (event.source !== iframeRef.current?.contentWindow) return;

      // Strict origin check for messages from this frame.
      if (event.origin !== v2.origin) {
        console.warn(
          `[KhalAppFrame] Ignored message from unexpected origin: ${event.origin} (expected ${v2.origin})`,
        );
        return;
      }

      const msg = event.data;
      if (!msg || typeof msg.type !== 'string') return;

      switch (msg.type) {
        case 'khal:ready':
          // `khal:ready` means the child has installed its message listener and
          // is asking the shell for init/auth. It is NOT an ack. Reply now and
          // keep replaying until the child sends `khal:ack`; otherwise late-
          // listener apps get stranded behind the shell spinner forever.
          sendInitToFrame();
          setState('ready');
          setErrorMsg(null);
          break;
        case 'khal:ack':
          readyRef.current = true;
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          if (initReplayRef.current) clearInterval(initReplayRef.current);
          if (initWatchdogRef.current) clearInterval(initWatchdogRef.current);
          setState('ready');
          setErrorMsg(null);
          break;
        case 'khal:notify':
          // Stub: forward to shell notification system
          console.log('[KhalAppFrame] App notification:', msg);
          break;
        case 'khal:title':
          // Stub: update window title
          if (typeof msg.title === 'string') {
            document.title = msg.title;
          }
          break;
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [v2?.origin, attempt, sendInitToFrame]);

  // Send khal:init when iframe loads and reset timeout
  const handleIframeLoad = useCallback(() => {
    if (!v2 || !iframeRef.current) return;

    initSentRef.current = true;
    readyRef.current = false;

    // Clear any prior timeout/replay from a previous iframe document. Keep the
    // independent watchdog alive: it is the recovery path if the load-scoped
    // replay window still misses the child app listener.
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (initReplayRef.current) clearInterval(initReplayRef.current);

    // Stop replay eventually; the iframe remains visible so apps that render
    // without khal:ready are not replaced by a parent-shell timeout screen.
    timeoutRef.current = setTimeout(() => {
      if (initReplayRef.current) clearInterval(initReplayRef.current);
    }, IFRAME_TIMEOUT_MS);

    // Send khal:init to the iframe. The message intentionally carries both
    // the original token/user/customer fields and the newer
    // launchToken/context aliases; build once per load so every replay is
    // byte-for-byte stable for the child app.
    const initMsg = buildInitMessage();
    if (!initMsg) return;

    // Fail closed: if origin is empty, do NOT send with '*' wildcard.
    // The origin MUST be validated from launchUrl metadata.
    if (!v2.origin) {
      console.error('[KhalAppFrame] Cannot send khal:init — origin is empty. postMessage targetOrigin must be explicit.');
      setState('error');
      setErrorMsg('App origin not configured');
      return;
    }

    const targetOrigin = v2.origin as string;

    try {
      const sendInit = () => {
        iframeRef.current?.contentWindow?.postMessage(initMsg, targetOrigin);
      };

      sendInit();
      // The iframe app can attach its message listener well after the browser
      // fires iframe onLoad on cold networks/big bundles. Replay init until the
      // app acknowledges khal:ready or until the readiness timeout trips. A
      // fixed 2s replay window was still leaving Felipe stuck on the app's
      // own "Connecting to KHAL…" screen.
      initReplayRef.current = setInterval(() => {
        if (readyRef.current) {
          if (initReplayRef.current) clearInterval(initReplayRef.current);
          return;
        }
        sendInit();
      }, 500);
      // Runtime v2 steel-thread apps may not yet implement the khal:ready
      // handshake. Do not hold the UI hostage behind an invisible iframe:
      // once the document loads and init is sent, show it. Apps that do send
      // khal:ready still flow through the listener above.
      setState('ready');
      setErrorMsg(null);
    } catch (err) {
      console.error('[KhalAppFrame] Failed to send khal:init', err);
      setState('error');
      setErrorMsg('Failed to communicate with app');
    }
  }, [v2, state, attempt, buildInitMessage]);

  // Reset init flag on retry
  const handleRetry = useCallback(() => {
    initSentRef.current = false;
    setState('loading');
    setErrorMsg(null);
    setAttempt((n) => n + 1);
  }, []);

  if (framePaused) {
    return (
      <section
        data-frame-paused="true"
        style={{
          ...loadingStyle,
          background: 'rgba(2, 6, 23, 0.72)',
          color: 'var(--khal-text, #ddd)',
        }}
      >
        <div style={{ fontWeight: 700 }}>{parkedAppId} is parked cold</div>
        <div style={{ fontSize: 13, opacity: 0.75, maxWidth: 480 }}>
          KHAL thermal guard released this iframe renderer. Resume it to restore the live app.
        </div>
        <div style={{ fontSize: 12, opacity: 0.55, maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {parkedLaunchLabel}
        </div>
        <button type="button" onClick={resumeFrame} style={retryButtonStyle}>
          Resume app
        </button>
      </section>
    );
  }

  // Invalid meta
  if (!v2) {
    return (
      <div style={errorStyle}>
        <div>Missing launch configuration for v2 app.</div>
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          Expected meta.appId and meta.launchUrl.
        </div>
      </div>
    );
  }

  // Error state
  if (state === 'error' || state === 'timeout') {
    return (
      <div style={errorStyle}>
        <div>{state === 'timeout' ? 'App Not Responding' : 'App Error'}</div>
        <div style={{ fontSize: 12, opacity: 0.7, maxWidth: 480 }}>
          {errorMsg || 'An unknown error occurred'}
        </div>
        <div style={{ fontSize: 12, opacity: 0.5 }}>{v2.launchUrl}</div>
        <button type="button" onClick={handleRetry} style={retryButtonStyle}>
          Retry
        </button>
      </div>
    );
  }

  // Keep one iframe instance alive across loading → ready. Unmounting the hidden
  // iframe after its first load can move replayed khal:init calls onto a fresh
  // document and recreate the exact listener race that leaves apps stuck on
  // "Connecting to KHAL…".
  return (
    <div style={containerStyle}>
      {state === 'loading' ? (
        <div style={loadingStyle}>
          <div className="khal-app-frame-spinner" />
          <div>Loading {v2.appId}…</div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>{v2.launchUrl}</div>
        </div>
      ) : null}
      <iframe
        ref={iframeRef}
        src={v2.launchUrl}
        style={state === 'loading' ? { ...frameStyle, display: 'none' } : frameStyle}
        title={v2.appId}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        onLoad={handleIframeLoad}
        onError={() => {
          setState('error');
          setErrorMsg('App frame encountered an error');
        }}
      />
    </div>
  );
};

KhalAppFrame.displayName = 'KhalAppFrame';